/**
 * Sales coaching's words for the backend's codes. The server sends codes;
 * what a trainee reads is decided here.
 */

import { aiColors } from '@euler/app/flows/ai-suite/theme';
import {
  CoachingCaseFormat,
  CoachingChannel,
  CoachingDifficulty,
  CoachingOpeningSide,
} from '@euler/model/coaching';

export const kLevelNames: Record<CoachingDifficulty, string> = {
  [CoachingDifficulty.Foundation]: '基础',
  [CoachingDifficulty.Intermediate]: '进阶',
  [CoachingDifficulty.Advanced]: '高阶',
  [CoachingDifficulty.Expert]: '专家',
};

export const kLevelColors: Record<CoachingDifficulty, string> = {
  [CoachingDifficulty.Foundation]: aiColors.green,
  [CoachingDifficulty.Intermediate]: aiColors.primary,
  [CoachingDifficulty.Advanced]: aiColors.violet,
  [CoachingDifficulty.Expert]: aiColors.red,
};

/** A library case's levels: differently authored customers. */
export const kCaseLevelHints: Record<CoachingDifficulty, string> = {
  [CoachingDifficulty.Foundation]:
    '一个明确的顾虑，证据清楚，客户自己能做决定。',
  [CoachingDifficulty.Intermediate]: '客户有一个没说出口的顾虑，需要你问出来。',
  [CoachingDifficulty.Advanced]:
    '限制条件更多，可能要家人同意，或者证据不完整。',
  [CoachingDifficulty.Expert]: '条件互相冲突，不卖也可能才是对的。',
};

/** The trainee's own story: how much the coach adds to it. */
export const kStoryLevelHints: Record<CoachingDifficulty, string> = {
  [CoachingDifficulty.Foundation]: '照你说的来练。',
  [CoachingDifficulty.Intermediate]: '加一个你不知道的顾虑。',
  [CoachingDifficulty.Advanced]: '加几个限制条件，比如预算紧或者家人要点头。',
  [CoachingDifficulty.Expert]: '条件互相冲突，不卖也可能才是对的。',
};

export const kFormatNames: Record<CoachingCaseFormat, string> = {
  [CoachingCaseFormat.Full]: '完整对练',
  [CoachingCaseFormat.Drill]: '专项小练',
};

export const kChannelNames: Record<CoachingChannel, string> = {
  [CoachingChannel.Counter]: '门店前台',
  [CoachingChannel.WorkshopWalkthrough]: '车间现场',
  [CoachingChannel.Phone]: '电话',
  [CoachingChannel.Wechat]: '微信',
};

export const kOpeningNames: Record<CoachingOpeningSide, string> = {
  [CoachingOpeningSide.Customer]: '客户先开口',
  [CoachingOpeningSide.Trainee]: '我先开口',
};

/*
 * Labels for what the server shows. A newer server may send a value this
 * build has no word for; it reads as a neutral fallback, never `undefined`.
 */
export function levelName(level: CoachingDifficulty): string {
  return kLevelNames[level] ?? '其他难度';
}

export function levelColor(level: CoachingDifficulty): string {
  return kLevelColors[level] ?? aiColors.mutedForeground;
}

export function formatName(format: CoachingCaseFormat): string {
  return kFormatNames[format] ?? '练习';
}

export function channelName(channel: CoachingChannel): string {
  return kChannelNames[channel] ?? '其他场合';
}

export function openingName(side: CoachingOpeningSide): string {
  return kOpeningNames[side] ?? '自由开场';
}

export function turnsLabel(turns: { min: number; max: number }): string {
  return turns.min === turns.max
    ? `约 ${turns.min} 个来回`
    : `约 ${turns.min}–${turns.max} 个来回`;
}
